/* eslint-disable max-classes-per-file */
/* eslint-disable no-mixed-operators */
/* eslint no-param-reassign: 0 */
import _ from 'underscore';
import type { NamedValue, NamespaceName, TypeName } from '../types';

/** Class representing a namespace */
export class Namespace<TValue extends object> {
  map: Record<string, TValue | Namespace<TValue>>;
  parent: Namespace<TValue> | null;

  /**
   * Create a namespace
   * @param {Namespace|null} parent - The parent namespace
   */
  constructor(parent: Namespace<TValue> | null) {
    this.map = {};
    this.parent = parent;
  }

  /**
   * Resolve an object by name
   * @param {String[]} segments - The name segments
   * @return {Object} The object registered with the given name
   */
  resolve(segments: string[]): TValue | Namespace<TValue> | null {
    const iterator = (
      ns: TValue | Namespace<TValue> | undefined,
      seg: string,
    ): TValue | Namespace<TValue> | undefined => {
      if (ns && ns instanceof Namespace) {
        return ns.map[seg];
      }
      return ns;
    };

    return _.reduce(segments, iterator, this)
      || this.parent && this.parent.resolve(segments);
  }

  /**
   * Register an object
   * @param {Object} obj - The object to be registered
   * @param {String[]} segments - The name segments for the object
   * @return {Void} Nothing to return
   */
  register(obj: TValue, segments: string[]) {
    const key = segments.pop();
    const iterator = (ns: Namespace<TValue>, seg: string) => {
      if (ns instanceof Namespace) {
        if (_.isUndefined(ns.map[seg])) {
          ns.map[seg] = new Namespace(ns);
        }
        return ns.map[seg] as Namespace<TValue>;
      }
      throw new Error(`There are conflicts when defining registry for ${segments.join('.')}`);
    };

    const nsTarget = _.reduce(segments, iterator, this);

    nsTarget.map[key as string] = obj;
  }

  /**
   * Iterate through all objects in this namespace
   * @param {Function} worker - The callback for each of the objects
   * @return {void} Nothing to return
   */
  each(worker: (value: TValue) => void) {
    _.each(this.map, obj => {
      if (obj instanceof Namespace) {
        obj.each(worker);
      } else {
        worker(obj);
      }
    });
  }
}

function isObjectWithName(obj: object): obj is object & NamedValue {
  return 'name' in obj && typeof obj.name === 'string';
}

/** Class representing a namespaced registry */
export class Registry<TValue extends object = object> {
  qualifiedNames: Map<TValue, TypeName>;
  rootNamespace: Namespace<TValue>;

  /**
   * Create a registry
   */
  constructor() {
    this.rootNamespace = new Namespace(null);
    // eslint-disable-next-line no-undef
    this.qualifiedNames = new Map();
  }

  /**
   * Resolve an object by name
   * @param {String} name - The name of the object
   * @param {String} [namespace=this.rootNamespace] - The base namespace to resolve against
   * @return {Object|null} The object registered with the name
   */
  resolve(
    name: TypeName,
    namespace?: NamespaceName | null,
  ): TValue | Namespace<TValue> | null {
    const ns = namespace
      ? this.rootNamespace.resolve(namespace.split('.'))
      : this.rootNamespace;
    const obj = ns instanceof Namespace ? ns.resolve(name.split('.')) : null;

    return obj || null;
  }

  resolveQualifiedName(
    name: TypeName,
    namespace?: NamespaceName | null,
  ): TypeName | null {
    const obj = this.resolve(name, namespace);
    if (obj instanceof Namespace || !obj) {
      return null;
    }

    return this.qualifiedNames.get(obj) || null;
  }

  /**
   * Register an object
   * @param {Object} obj - The object to be registered
   * @param {String} [name=obj.name] - The qualified name for the object
   * @return {void} Nothing to return
   */
  register(obj: TValue, name?: TypeName) {
    const realName = !name && isObjectWithName(obj) ? obj.name : name;
    if (typeof realName === 'undefined') {
      throw new Error(`name not passed in and obj ${JSON.stringify(obj)} doesn't have name property`);
    }
    this.rootNamespace.register(obj, realName.split('.'));
    this.qualifiedNames.set(obj, realName);
  }

  /**
   * Iterate through all registered objects
   * @param {Function} worker - The callback for each of the objects
   * @return {void} Nothing to return
   */
  each(worker: (value: TValue) => void) {
    this.rootNamespace.each(worker);
  }

  /**
   * Get namespace from a qualified name
   * @param {String} name - The qualified name
   * @return {String} The namespace
   */
  static getNamespace(name: TypeName) {
    const segments = name.split('.');

    segments.pop();
    return segments.join('.');
  }

  /**
   * Get short name from a qualified name
   * @param {String} name - The qualified name
   * @return {String} The short name
   */
  static getShortName(name: TypeName) {
    return _.last(name.split('.'));
  }

  static getQualifiedName(name: TypeName, namespace?: NamespaceName | null) {
    return namespace ? `${namespace}.${name}` : name;
  }
}
